import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP admin welcome-letter manager.
// - Lists active SIP supervisors with checkboxes + per-row print + check/uncheck all.
// - Per-row "Print" rotates that supervisor's SIPPassword (new salt + hash + plaintext
//   SIPRandomCode) and opens a single-letter PDF.
// - "Print Selected" does the same for all checked rows in one combined PDF.
function WelcomeLetterManager() {
    const [supervisors, setSupervisors] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');
    const [siteFilter, setSiteFilter] = useState('');

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getSupervisors();
            setSupervisors(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            flash('Failed to load supervisors.', 'error');
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const filtered = useMemo(() => (
        siteFilter ? supervisors.filter((s) => s.site === siteFilter) : supervisors
    ), [supervisors, siteFilter]);

    const sites = useMemo(() => (
        [...new Set(supervisors.map((s) => s.site).filter(Boolean))].sort()
    ), [supervisors]);

    const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.employeeID));

    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAllFiltered = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach((s) => next.delete(s.employeeID));
            } else {
                filtered.forEach((s) => next.add(s.employeeID));
            }
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const openPdfBlob = (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    // axios responseType:'blob' delivers BadRequest(ex.Message) as a Blob, not
    // a string — pull the text out so the actual server-side error surfaces in
    // the UI instead of just "Failed to generate letter."
    const extractErrorMessage = async (err) => {
        try {
            const data = err?.response?.data;
            if (data instanceof Blob) {
                const text = await data.text();
                if (text) return text.slice(0, 500);
            }
            if (typeof data === 'string' && data) return data.slice(0, 500);
            if (data?.message) return String(data.message).slice(0, 500);
            return err?.message || 'Unknown error';
        } catch {
            return 'Unknown error';
        }
    };

    const printOne = async (employeeId) => {
        if (!window.confirm('Print a new welcome letter for this supervisor? This rotates their initial password.')) return;
        setBusy(true); setMsg('');
        try {
            const res = await adminAPI.printWelcomeLetter(employeeId);
            openPdfBlob(res.data);
            await reload();
            flash('Letter generated.', 'success');
        } catch (err) {
            const detail = await extractErrorMessage(err);
            flash(`Failed to generate letter: ${detail}`, 'error');
        } finally { setBusy(false); }
    };

    const printSelected = async () => {
        const ids = [...selected];
        if (ids.length === 0) return;
        if (!window.confirm(`Generate welcome letters for ${ids.length} supervisor${ids.length === 1 ? '' : 's'}?\n\nThis will rotate each one's initial password.`)) return;
        setBusy(true); setMsg('');
        try {
            const res = await adminAPI.printWelcomeLetters(ids);
            openPdfBlob(res.data);
            setSelected(new Set());
            await reload();
            flash(`Generated ${ids.length} letter${ids.length === 1 ? '' : 's'}.`, 'success');
        } catch (err) {
            const detail = await extractErrorMessage(err);
            flash(`Failed to generate letters: ${detail}`, 'error');
        } finally { setBusy(false); }
    };

    if (loading) return <div style={styles.muted}>Loading…</div>;

    if (supervisors.length === 0) {
        return (
            <div style={styles.card}>
                <h2 style={styles.cardTitle}>No active SIP supervisors</h2>
                <p style={styles.muted}>Once SIP supervisors are set up, they'll appear here.</p>
            </div>
        );
    }

    const selectedCount = selected.size;

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.headerRow}>
                    <div>
                        <div style={styles.eyebrow}>Welcome Letters</div>
                        <div style={styles.title}>Print &amp; rotate passwords</div>
                        <div style={styles.muted}>
                            {supervisors.length} supervisor{supervisors.length === 1 ? '' : 's'}
                            {' · '}{selectedCount} selected
                        </div>
                    </div>
                    <div style={styles.actions}>
                        <button onClick={toggleAllFiltered} disabled={filtered.length === 0} style={btn('#6b7280', filtered.length === 0)}>
                            {allFilteredSelected ? 'Unselect all (visible)' : 'Select all (visible)'}
                        </button>
                        <button onClick={clearSelection} disabled={selectedCount === 0} style={btn('#9ca3af', selectedCount === 0)}>
                            Clear selection
                        </button>
                        <button onClick={printSelected} disabled={busy || selectedCount === 0} style={btn('#15803d', busy || selectedCount === 0)}>
                            Print selected ({selectedCount})
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ ...styles.card, marginTop: 12 }}>
                <div style={styles.bulkRow}>
                    <div style={styles.bulkLabel}>Filter by Site:</div>
                    <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} style={styles.input}>
                        <option value="">(all sites)</option>
                        {sites.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div style={{ flex: 1 }} />
                    <div style={styles.muted}>{filtered.length} of {supervisors.length} shown</div>
                </div>
            </div>

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            <div style={styles.tableCard}>
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            <th style={{ ...styles.th, width: 36, textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={allFilteredSelected}
                                    onChange={toggleAllFiltered}
                                    aria-label="Toggle all"
                                />
                            </th>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Site</th>
                            <th style={styles.th}>Last password change</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((s) => {
                            const id = s.employeeID;
                            const isSelected = selected.has(id);
                            return (
                                <tr key={id} style={{ borderBottom: '1px solid #f3f4f6', background: isSelected ? '#fefce8' : 'transparent' }}>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleOne(id)}
                                            aria-label={`Select ${s.firstName} ${s.lastName}`}
                                        />
                                    </td>
                                    <td style={styles.td}>{(s.lastName || '').toString()}{s.lastName && s.firstName ? ', ' : ''}{(s.firstName || '').toString()}</td>
                                    <td style={styles.td}>{s.site || '—'}</td>
                                    <td style={styles.td}>{fmtDateTime(s.sipPasswordChanged) || <span style={styles.muted}>never</span>}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>
                                        <button onClick={() => printOne(id)} disabled={busy} style={btn('#2563eb', busy)}>
                                            Print
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

function fmtDateTime(s) {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    cardTitle: { marginTop: 0, marginBottom: 6, fontSize: 18, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2 },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
    actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    tableCard: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflowX: 'auto', marginTop: 4 },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '10px 12px', fontSize: 14, color: '#111827' }
};

export default WelcomeLetterManager;
